import { proximaApi } from '../proximaApi'
import { type SelectSettlementOptionPayloadDto } from '../schema-dtos/SettlementPayloadDtos'
import { assetHolderApiMapper } from './assetHolderApiMapper'
import { type AssetHoldingPortfolio } from '../schema-dtos/DashboardDomainDtos'
import { type AssetHolderSettlementDto, type AssetHolderSettlementIdentifier } from '../schema-dtos/SettlementDomainDtos'
import {
  type AssetHolderUpdateDto,
  type AssetHolderUpdatePayload,
  type UpdateAssetHolderEmailAddressPayload
} from '../schema-dtos/AssetHolderCrudDtos'
import { type ActivityStatementSignedUrl } from '../schema-dtos/AssetHolderFinanceDomainDtos'
import { type GetActivityStatementUrlPayload } from '../schema-dtos/AssetHolderFinancePayloadDtos'

export interface AssetHolderIdProps {
  assetHolderId: number
}

const SUB_URL = '/asset-holdings/asset-holders'

export const assetHolderApi = proximaApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboard: builder.query({
      query: ({ assetHolderId }: AssetHolderIdProps) => ({
        url: `${SUB_URL}/${assetHolderId}/dashboard`
      }),
      providesTags: ['Dashboard'],
      transformResponse: (response: AssetHoldingPortfolio, meta, arg: AssetHolderIdProps) =>
        assetHolderApiMapper().dashboardMapper(response, meta, arg)
    }),

    // ================================================================================================================

    getCurrentSettlements: builder.query({
      query: ({ assetHolderId }: AssetHolderIdProps) => ({
        url: `${SUB_URL}/${assetHolderId}/settlements`
      }),
      providesTags: ['AssetHolderSettlement'],
      transformResponse: (response: AssetHolderSettlementDto[], meta, arg: AssetHolderIdProps) => response
    }),

    acceptSettlement: builder.mutation<AssetHolderSettlementIdentifier, SelectSettlementOptionPayloadDto>({
      query: (body: SelectSettlementOptionPayloadDto) => ({
        url: `${SUB_URL}/${body.assetHolderId}/settlements/accept`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['AssetHolderSettlement', 'AssetHolderFinancials']
    }),

    // ================================================================================================================

    getAssetHolder: builder.query<AssetHolderUpdateDto, Partial<void>>({
      query: () => ({
        url: `${SUB_URL}/profile`
      }),
      providesTags: ['Dashboard', 'AssetHolderProfile']
    }),

    updateAssetHolderDetails: builder.mutation<undefined, Partial<{ assetHolderId: number, body: AssetHolderUpdatePayload }>>({
      query: (payload) => ({
        url: `${SUB_URL}/${payload.assetHolderId}`,
        method: 'PUT',
        body: payload.body
      }),
      invalidatesTags: ['AssetHolderProfile', 'AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    updateEmailAddress: builder.mutation<undefined, Partial<UpdateAssetHolderEmailAddressPayload>>({
      query: (payload) => ({
        url: `${SUB_URL}/${payload.assetHolderId}/update-email`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Dashboard', 'AssetHolderProfile']
    }),

    // ================================================================================================================

    getCellOwnerAgreementPdfUrl: builder.query({
      query: ({ assetHolderId, projectId }) => ({
        url: `${SUB_URL}/${assetHolderId}/cell-owner-agreements/project/${projectId}/pdf-url`,
        params: { }
      })
    }),

    // ================================================================================================================

    getActivityStatementUrl: builder.query<ActivityStatementSignedUrl, Partial<{ assetHolderId: number } & GetActivityStatementUrlPayload>>({
      query: (payload: any) => ({
        url: `${SUB_URL}/${payload.assetHolderId}/activity-statement/generate-signed-url-for-view?startDate=${payload.startDate}&endDate=${payload.endDate}&otp=${payload.otp}`,
        method: 'GET'
      })
    })

  }),
  overrideExisting: false
})

export const useGetDashboardQuery = assetHolderApi.endpoints.getDashboard.useQuery

export const useGetCurrentSettlementsQuery = assetHolderApi.endpoints?.getCurrentSettlements.useQuery
export const useAcceptSettlementMutation = assetHolderApi.endpoints?.acceptSettlement.useMutation

export const getAssetHolderLazyQuery = assetHolderApi.endpoints.getAssetHolder.useLazyQuery
export const getAssetHolderQuery = assetHolderApi.endpoints.getAssetHolder.useQuery
export const updateAssetHolderDetailsMutation = assetHolderApi.endpoints.updateAssetHolderDetails.useMutation
export const useUpdateEmailAddressMutation = assetHolderApi.endpoints.updateEmailAddress.useMutation

export const useCellOwnerAgreementsGetPdfUrlLazyQuery = assetHolderApi.endpoints.getCellOwnerAgreementPdfUrl.useLazyQuery

export const useGetActivityStatementUrlQuery = assetHolderApi.endpoints.getActivityStatementUrl.useLazyQuery
